import {
  type CSSProperties,
  Fragment,
  type ReactNode,
  useContext,
} from "react";
import {
  CheckIcon,
  CircleAlertIcon,
  CircleQuestionMarkIcon,
  PlusIcon,
  XIcon,
} from "lucide-react";

import { MatchDocumentSettlementBasisEnum } from "@/__generated__/graphql";
import { Icon } from "@/components/atoms/Icon";
import { Queries } from "@/constants/queries";
import { EnumColors } from "@/constants/values";
import { useBackendQuery } from "@/hooks/useBackendQuery";
import { useEnumColor } from "@/hooks/useEnumColor";
import { State } from "@/state";
import { Utils } from "@/utils";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/components/ui/utils";
import { Typography, typographyVariants } from "@/components/ui/typography";

import {
  buildChangelistProposalPresentation,
  criteriaLabel,
  suggestionConclusion,
} from "./TriageChangelistProposalRow";
import { ruleWhenDescriptionBody } from "../RulesView/RuleWhenDescription";
import {
  isFilterGroup,
  ruleConditionsToFilterGroup,
} from "../RulesView/utils/filterGroup";
import {
  buildBreakdownColumns,
  matchDocumentFindCriteriaLines,
} from "./TriageMatchDocumentCriteriaCards";
import {
  type TriageDetail,
  TriageDetailContext,
  type TriageSuggestionStub,
} from "./TriageView.context";
import { JOB_ACTIONS } from "./TriageView.keys";

type Reason = {
  /** What it is about: an amount, a date, a document - which says where it comes in the summary. */
  about:
    | "amount"
    | "date"
    | "description"
    | "account"
    | "document"
    | "payment"
    | "suggestion"
    | "rule"
    | "parts"
    | "history"
    | "note";
  label: string;
  value: string;
};

/** What every table a suggestion opens to is set out by, across. */
const TABLE_COLUMNS = [
  "Description",
  "Amount",
  "Tax",
  "Total",
  "Date",
] as const;
type TableColumn = (typeof TABLE_COLUMNS)[number];

/** How wide each column is let be: the description takes what is left. */
const TABLE_COLUMN_WIDTHS: Record<TableColumn, string> = {
  // The words give way first - wrapped at their breaks - so the figures
  // keep theirs whole in a narrow panel, before the table scrolls at all:
  // to 6rem of words, past its cell's room either side.
  Description: "minmax(calc(6rem + 20px), 1fr)",
  Amount: "max-content",
  Tax: "max-content",
  Total: "max-content",
  Date: "max-content",
};

/**
 * A document under its lines: the line over it runs over its first line
 * instead, and none comes between its lines and it - in the table, and in a
 * business's box in it alike.
 */
const TABLE_LINES_CLASS = cn(
  "[&>:not([data-part='line'])+[data-part='line']>*]:pt-1.5 [&>:not([data-part='line'])+[data-part='line']>*]:[border-top-width:1px] [&>:not([data-part='line'])+[data-part='line']>*]:[border-top-style:solid] [&>:not([data-part='line'])+[data-part='line']>*]:border-border",
  "[&>[data-part='line']+[data-testid='triage-suggestion-table-row']>*]:pt-0.5 [&>[data-part='line']+[data-testid='triage-suggestion-table-row']>*]:[border-top:none]",
);

/**
 * What a suggestion has going for it and against it, worked out once: what it
 * opens to sets its figures beside the document or payment they were compared
 * with (``TriageSuggestionContext``).
 */
export function triageSuggestionReasons(
  suggestion: TriageSuggestionStub,
  triage: TriageDetail,
) {
  const presentation = buildChangelistProposalPresentation(suggestion, triage);
  const job = presentation.job;
  const amount = Number(triage.amount ?? 0);
  const paid = Utils.fCurrency(Math.abs(amount), false, true);
  const source = (suggestion.source ?? "").toLowerCase();

  // Which day a moment falls on, as the dates on screen show it (they are
  // shown in UTC): two payments seven hours apart across midnight are a day
  // apart, not the same day.
  const calendarDay = (value: string) => {
    const moment = new Date(value);
    return (
      Date.UTC(
        moment.getUTCFullYear(),
        moment.getUTCMonth(),
        moment.getUTCDate(),
      ) / 86_400_000
    );
  };

  // What it has going for it, and what it has against it.
  const fits: Reason[] = [];
  const doubts: Reason[] = [];
  const isModel = source === "ai" || source === "agent";
  // The rule every entity is given, which looks at every bank row: that it
  // "matched" says nothing, so it is not spoken of as a rule of the user's.
  const ruleName =
    (suggestion.rule?.metadata as { system_rule?: string } | null)
      ?.system_rule != null
      ? null
      : presentation.ruleName;
  if (!ruleName && isModel) {
    doubts.push({
      about: "rule",
      label: "Rule",
      value:
        "None of yours covers this payment; this is the AI's reading of it",
    });
  }

  // One thing set beside another, a row per thing compared.
  const comparisons: Array<{
    key: string;
    title: string;
    other: string;
    rows: Array<{
      about: Reason["about"];
      label: string;
      payment: string;
      other: string;
      verdict: string;
      matched: boolean;
    }>;
  }> = [];

  if (job === "MATCH_DOCUMENT") {
    for (const document of presentation.linkedDocuments) {
      const columns = presentation.matchDocumentDefinition
        ? buildBreakdownColumns(
            presentation.matchDocumentDefinition,
            document,
            amount,
            triage.effectiveDate,
            triage.description,
            presentation.matchedCriteria,
            presentation.configuredCriteria,
          )
        : [];
      const rows = columns.map((column) => ({
        about: (column.id === "amount"
          ? "amount"
          : column.id === "date_window"
            ? "date"
            : "description") as Reason["about"],
        label: column.label,
        payment: column.bank ?? "—",
        other: column.doc ?? "—",
        verdict: column.match ?? "—",
        matched: column.matched,
      }));
      // No rule's definition to compare by (the model matched it): the two
      // things anyone would check first.
      if (rows.length === 0) {
        const total = document.amountTotal ?? document.amountDue ?? null;
        if (total != null) {
          // In the payment's currency: one in another turned at the official
          // rate. One with no rate published yet is not compared at all.
          const own = Math.abs(Number(total));
          const inPayment = own * (document.exchangeRate ?? 1);
          const gap = Math.abs(Math.abs(amount) - inPayment);
          const unrated =
            document.exchangeRateSource != null &&
            document.exchangeRate == null;
          rows.push({
            about: "amount" as const,
            label: "Amount",
            payment: paid,
            other:
              document.exchangeRate != null
                ? `${document.currency} ${own.toFixed(2)} ≈ ${Utils.fCurrency(inPayment, false, true)}`
                : unrated
                  ? `${document.currency} ${own.toFixed(2)}`
                  : Utils.fCurrency(own, false, true),
            verdict: unrated
              ? "No rate"
              : gap < 0.005
                ? "Exact"
                : `${Utils.fCurrency(gap, false, true)} apart`,
            matched: !unrated && gap < 0.005,
          });
        }
        // The day it was issued: the day it was imported says nothing of it.
        const documentDate = document.dateIssued ?? null;
        if (documentDate && triage.effectiveDate) {
          // Before the payment by as long as bills wait to be paid - weeks,
          // not months - or after it by as long as a receipt takes to come.
          const before =
            calendarDay(triage.effectiveDate) - calendarDay(documentDate);
          const days = Math.abs(before);
          rows.push({
            about: "date" as const,
            label: "Date",
            payment: Utils.fDate(new Date(triage.effectiveDate)),
            other: Utils.fDate(new Date(documentDate)),
            verdict:
              days === 0
                ? "Same day"
                : `${days} day${days === 1 ? "" : "s"} ${before > 0 ? "before" : "after"} the payment`,
            matched: before >= -7 && before <= 60,
          });
        }
      }
      for (const row of rows) {
        if (row.matched) {
          fits.push({
            about: row.about,
            label: row.label,
            value: row.verdict,
          });
        } else {
          doubts.push({
            about: row.about,
            label: row.label,
            value: row.verdict,
          });
        }
      }
      const total =
        document.amountTotal != null ? Number(document.amountTotal) : null;
      const due =
        document.amountDue != null ? Number(document.amountDue) : null;
      if (total != null && due != null && due < total) {
        doubts.push({
          about: "document",
          label: "Balance",
          value:
            due <= 0
              ? `${document.title} is already paid in full`
              : `${document.title} is partly paid, with ${Utils.fCurrency(due, false, true)} of ${Utils.fCurrency(total, false, true)} still owing`,
        });
      }
      comparisons.push({
        key: document.id,
        title: document.title,
        other: "Document",
        rows,
      });
    }
    // What the rule also asked for that is not a value to set side by side.
    // (Nor is "document": that there is one is the suggestion itself.)
    const compared = new Set(["amount", "date_window", "wording", "document"]);
    for (const criterion of presentation.configuredCriteria) {
      if (compared.has(criterion)) continue;
      if (presentation.matchedCriteria.includes(criterion)) {
        fits.push({
          about: "rule",
          label: criteriaLabel(criterion),
          value: "Matches",
        });
      } else {
        doubts.push({
          about: "rule",
          label: criteriaLabel(criterion),
          value: "Does not match",
        });
      }
    }
  }
  // (Only a document match lists criteria here. What the other rules carry
  // under that name are their settings - exclude reconciled, look back ninety
  // days - which a payment neither matches nor fails.)

  if (job === "DUPLICATE" || job === "PAIR" || job === "TRANSFER") {
    for (const option of suggestion.ruleSuggestionTransactionOptions ?? []) {
      const other = option.transaction;
      if (!other) continue;
      const otherAmount = Number(other.amount ?? 0);
      // A duplicate is the same payment twice; a transfer or a pair is one
      // payment seen from both of its ends.
      const amountsAgree =
        job === "DUPLICATE"
          ? Math.abs(otherAmount - amount) < 0.005
          : Math.abs(otherAmount + amount) < 0.005;
      const days =
        other.effectiveDate && triage.effectiveDate
          ? Math.abs(
              calendarDay(triage.effectiveDate) -
                calendarDay(other.effectiveDate),
            )
          : null;
      const sameWords =
        (other.description ?? "").trim().toLowerCase() ===
        (triage.description ?? "").trim().toLowerCase();
      const rows = [
        {
          about: "amount" as const,
          label: "Amount",
          payment: Utils.fCurrency(amount, false, true),
          other: Utils.fCurrency(otherAmount, false, true),
          verdict: amountsAgree
            ? job === "DUPLICATE"
              ? "The same"
              : "Equal and opposite"
            : "Different",
          matched: amountsAgree,
        },
        ...(days != null
          ? [
              {
                about: "date" as const,
                label: "Date",
                payment: Utils.fDate(new Date(triage.effectiveDate)),
                other: Utils.fDate(new Date(other.effectiveDate)),
                verdict:
                  days === 0
                    ? "Same day"
                    : `${days} day${days === 1 ? "" : "s"} apart`,
                matched:
                  days === 0 ||
                  presentation.matchedCriteria.includes("date_window"),
              },
            ]
          : []),
        {
          about: "description" as const,
          label: "Description",
          payment: triage.description?.trim() || "—",
          other: other.description?.trim() || "—",
          verdict: sameWords ? "The same" : "Different",
          // Two ends of a transfer are worded by two banks.
          matched: sameWords || job !== "DUPLICATE",
        },
        {
          about: "account" as const,
          label: "Account",
          payment: triage.financialAccount?.name?.trim() || "—",
          other: other.financialAccount?.name?.trim() || "—",
          verdict:
            triage.financialAccount?.identifier ===
            other.financialAccount?.identifier
              ? "The same"
              : "Different",
          // The same payment twice is in one account; a transfer's two ends
          // are in two.
          matched:
            (triage.financialAccount?.identifier ===
              other.financialAccount?.identifier) ===
            (job === "DUPLICATE"),
        },
      ];
      for (const row of rows) {
        if (row.matched) {
          if (row.label !== "Description" || sameWords) {
            fits.push({
              about: row.about,
              label: row.label,
              value: row.verdict,
            });
          }
        } else {
          doubts.push({
            about: row.about,
            label: row.label,
            value: row.verdict,
          });
        }
      }
      comparisons.push({
        key: other.id,
        title: other.description?.trim() || "The other payment",
        other: "Other payment",
        rows,
      });
    }
  }

  // A split's parts: every line but the bank's own.
  const bankAccountIdentifier =
    triage.financialAccount?.chartedAccount?.identifier ?? null;
  const parts =
    job === "SPLIT"
      ? (suggestion.lines ?? []).filter(
          (line) =>
            line.chartedAccount?.identifier !== bankAccountIdentifier &&
            Number(line.amount ?? 0) !== 0,
        )
      : [];
  const partsTotal = parts.reduce(
    (sum, line) => sum + Math.abs(Number(line.amount ?? 0)),
    0,
  );
  if (parts.length > 0) {
    if (Math.abs(partsTotal - Math.abs(amount)) < 0.005) {
      fits.push({
        about: "parts",
        label: "Parts",
        value: "Add up to the payment",
      });
    } else {
      doubts.push({
        about: "parts",
        label: "Parts",
        value: `Come to ${Utils.fCurrency(partsTotal, false, true)}, and the payment is ${paid}`,
      });
    }
  }
  if (job === "CATEGORIZE") {
    const categorize = (
      suggestion.metadata as {
        categorize?: { match_count?: number; sample_total?: number };
      } | null
    )?.categorize;
    if (
      categorize?.match_count != null &&
      categorize.sample_total != null &&
      categorize.sample_total > 0 &&
      categorize.match_count / categorize.sample_total < 0.5
    ) {
      doubts.push({
        about: "history",
        label: "History",
        value: `Only ${categorize.match_count} of ${categorize.sample_total} recent bookings like this went there`,
      });
    }
  }

  const reason = presentation.selectionRationale || presentation.rationale;
  if (reason && (isModel || comparisons.every((c) => c.rows.length === 0))) {
    fits.unshift({
      about: "note",
      label: isModel ? "AI" : "Reason",
      value: reason,
    });
  }

  // What else is on the table. A suggestion read alone looks like the answer;
  // beside the others it is one of the answers.
  const documentIds = new Set(
    presentation.linkedDocuments.map((document) => document.id),
  );
  const conclusion = suggestionConclusion(suggestion);
  for (const other of triage.suggestions ?? []) {
    if (other.id === suggestion.id) continue;
    // Another rule that came to the same is this row, not another answer.
    if (suggestionConclusion(other) === conclusion) continue;
    const otherPresentation = buildChangelistProposalPresentation(
      other,
      triage,
    );
    // Another way to post it in the same business, lent alike, is on its
    // card, right under it.
    if (
      presentation.payerSide != null &&
      otherPresentation.payerSide === presentation.payerSide
    ) {
      continue;
    }
    if (
      job === "MATCH_DOCUMENT" &&
      otherPresentation.job === "MATCH_DOCUMENT"
    ) {
      const shared = otherPresentation.linkedDocuments.filter((document) =>
        documentIds.has(document.id),
      );
      const others = otherPresentation.linkedDocuments.filter(
        (document) => !documentIds.has(document.id),
      );
      for (const document of shared) {
        doubts.push({
          about: "document",
          label: "Also suggested",
          value:
            others.length > 0
              ? `${document.title} is in another suggestion here too, together with ${others.map((row) => row.title).join(" and ")}`
              : `${document.title} is in another suggestion here too`,
        });
      }
      if (shared.length === 0) {
        for (const document of others) {
          doubts.push({
            about: "document",
            label: "Other document",
            value: `${document.title}${document.amountLabel ? ` (${document.amountLabel})` : ""} is also suggested for this payment`,
          });
        }
      }
      continue;
    }
    if (otherPresentation.job !== job) {
      doubts.push({
        about: "suggestion",
        label: "Other suggestion",
        value: `Would ${JOB_ACTIONS[otherPresentation.job] ?? "do something else with it"}`,
      });
      continue;
    }
    if (job === "DUPLICATE" || job === "PAIR") {
      const counterpart =
        other.ruleSuggestionTransactionOptions?.[0]?.transaction;
      doubts.push({
        about: "payment",
        label: "Other suggestion",
        value: `Sets it against a different payment${counterpart?.description?.trim() ? `, ${counterpart.description.trim()}` : ""}`,
      });
      continue;
    }
    const elsewhere = (other.lines ?? [])
      .filter(
        (line) =>
          line.chartedAccount?.identifier !== bankAccountIdentifier &&
          Number(line.amount ?? 0) !== 0,
      )
      .map((line) => line.chartedAccount?.name?.trim())
      .filter(Boolean);
    doubts.push({
      about: "suggestion",
      label: "Other suggestion",
      value:
        elsewhere.length > 0
          ? `Would send it to ${elsewhere.join(" and ")}`
          : "Another of the same kind is on this payment",
    });
  }

  // How a column compares, across all that is set beside the payment: said
  // once, each different thing said where several differ - in the table it
  // opens to, and on its row as a chip.
  const verdictFor = (about: Reason["about"]) => {
    const found = comparisons.flatMap((comparison) =>
      comparison.rows.filter((row) => row.about === about),
    );
    return found.length > 0
      ? {
          text: [...new Set(found.map((row) => row.verdict))].join(", "),
          matched: found.every((row) => row.matched),
        }
      : null;
  };

  return {
    presentation,
    job,
    ruleName,
    fits,
    doubts,
    comparisons,
    verdictFor,
  };
}

/**
 * What a suggestion opens to in the list: one table, the same for every kind -
 * type, amount, description and date across; the payment, then what it is set
 * beside, down, each value with how it compares under it in red where it
 * does not agree. A document match lists the documents it would settle and
 * adds them up against the payment; a duplicate, a transfer or a pair sets the
 * other payment under this one, their wording marked where it differs. A
 * suggestion with nothing to compare - a categorization - does not open: its
 * card says what it would do.
 *
 * The entry it would book is not here: Review already shows that.
 */
export function TriageSuggestionContext({
  suggestion,
  triage,
}: {
  suggestion: TriageSuggestionStub;
  triage: TriageDetail;
}) {
  // Who else is offered this suggestion's documents. Its own question, asked
  // of the one suggestion being looked at and only when it has documents.
  const { data: rivalData } = useBackendQuery(
    Queries.RULE_SUGGESTION_COMPETING_QUERY,
    {
      variables: { id: suggestion.id },
      skip: (suggestion.ruleSuggestionDocumentOptions ?? []).length === 0,
    },
  );

  const { presentation, job, comparisons, verdictFor } =
    triageSuggestionReasons(suggestion, triage);
  // The same document, offered to another payment too, can only be taken
  // once: said under the document in a match's table.
  const rivals =
    rivalData?.node?.__typename === "RuleSuggestionObject"
      ? rivalData.node.competingSuggestions
      : [];
  const offeredElsewhere: Record<string, string[]> = {};
  for (const rival of rivals) {
    for (const option of rival.ruleSuggestionDocumentOptions ?? []) {
      const document = presentation.linkedDocuments.find(
        (row) => row.id === option.document?.id,
      );
      if (!document) continue;
      const elsewhere = `${rival.transaction.description?.trim() || "a payment"}, ${Utils.fCurrency(Math.abs(Number(rival.transaction.amount ?? 0)), false, true)}${rival.transaction.effectiveDate ? ` on ${Utils.fDate(new Date(rival.transaction.effectiveDate))}` : ""}`;
      (offeredElsewhere[document.id] ??= []).push(
        `Also offered to ${elsewhere}`,
      );
    }
  }

  const amount = Number(triage.amount ?? 0);
  // The payments a matched document is paid by already, each with its share
  // of it (``ChangelistProposalPresentation.paidElsewhere``). Both paid it -
  // where this fits in what is left - they are added to this one against the
  // document; else the match moves here, and they move off it.
  const detail = useContext(TriageDetailContext);
  const paidBefore = presentation.paidElsewhere.flatMap((one) =>
    one.shares.map((share) => ({
      payment: share.transaction,
      paid: share.amount,
    })),
  );
  const bothPaid =
    paidBefore.length > 0 &&
    presentation.canBothPay &&
    (detail?.selections.matchesKeptBySuggestion?.[suggestion.identifier] ??
      true);
  const paidBeforeTotal = bothPaid
    ? paidBefore.reduce((sum, row) => sum + row.paid, 0)
    : 0;
  // The other payments that may pay the rest of its document - a receipt paid
  // on two cards - offered under its table: each added to it by hand, then
  // counted against the document and booked with this one.
  const { data: partsData } = useBackendQuery(
    Queries.RULE_SUGGESTION_OTHER_PARTS_QUERY,
    {
      variables: { id: suggestion.id },
      skip: (suggestion.ruleSuggestionDocumentOptions ?? []).length === 0,
    },
  );
  const parts =
    partsData?.node?.__typename === "RuleSuggestionObject"
      ? (partsData.node.ruleSuggestionDocumentOptions ?? []).flatMap(
          (option) => option.otherParts ?? [],
        )
      : [];
  const added =
    detail?.selections.partsBySuggestion?.[suggestion.identifier] ?? [];
  const partsBookedWith = parts.filter((part) =>
    added.includes(part.transaction.identifier),
  );
  const setAdded = (identifiers: string[]) =>
    detail?.setSelection("partsBySuggestion", (current) => ({
      ...current,
      [suggestion.identifier]: identifiers,
    }));
  const others = comparisons.flatMap((comparison) => {
    const other = (suggestion.ruleSuggestionTransactionOptions ?? []).find(
      (option) => option.transaction?.id === comparison.key,
    )?.transaction;
    return other ? [{ comparison, other }] : [];
  });
  // Marked word by word against the one payment it is set beside; a
  // document's name is not the bank's memo written another way.
  const against =
    job !== "MATCH_DOCUMENT" && others.length === 1
      ? (others[0].other.description?.trim() ?? "")
      : null;
  const payment = triage.description?.trim() || "—";
  const documents = presentation.linkedDocuments;
  // What the payment was paid in: its own currency, its bank account's, or -
  // nothing more particular saying - its business's.
  const paidIn =
    (
      triage.amountCurrency ??
      (triage.financialAccount && "currency" in triage.financialAccount
        ? triage.financialAccount.currency
        : null) ??
      triage.entity?.currency
    )?.toUpperCase() ?? null;
  // Written alike on every row, the bank's sign dropped, so the same amount
  // reads as the same - and, where a document is in another currency than the
  // payment's, each in its own as it is written: CA$, US$.
  const isInTwoCurrencies =
    job === "MATCH_DOCUMENT" &&
    documents.some((document) => document.exchangeRateSource != null);
  // Where a document charges tax, a column of it beside the amounts, and each
  // row added across in a total: its lines before tax and the tax on them come
  // to its total, what is set against the payment's.
  const isTaxed =
    job === "MATCH_DOCUMENT" &&
    documents.some((document) => Number(document.amountTax ?? 0) !== 0);
  const formatAmount = (value: number, currency: string | null = paidIn) =>
    isInTwoCurrencies && currency
      ? new Intl.NumberFormat("en-GB", { style: "currency", currency }).format(
          Math.abs(value),
        )
      : Utils.fCurrency(Math.abs(value), false, true);
  // What a document comes to in the payment's currency: one in another turned
  // at the official rate the day the payment posted.
  const ownTotalOf = (document: (typeof documents)[number]) =>
    Math.abs(Number(document.amountTotal ?? document.amountDue ?? 0));
  const totalOf = (document: (typeof documents)[number]) =>
    ownTotalOf(document) * (document.exchangeRate ?? 1);
  const together = documents.reduce(
    (sum, document) => sum + totalOf(document),
    0,
  );
  // What pays the documents: this payment, the others where both paid it, and
  // the other parts booked with it.
  const paying =
    Math.abs(amount) +
    paidBeforeTotal +
    partsBookedWith.reduce(
      (sum, part) => sum + Math.abs(Number(part.transaction.amount ?? 0)),
      0,
    );
  const gap = Math.abs(paying - together);
  // More than one on a side: what is between them is said of them together,
  // which way it runs.
  const isSeveral =
    bothPaid || partsBookedWith.length > 0 || documents.length > 1;
  const isOver = paying > together;
  // Turned at a rate, how far apart they are is said as a share of what the
  // documents come to as well: a bank's own rate is a few percent off the
  // official one.
  const share =
    documents.some((document) => document.exchangeRate != null) && together > 0
      ? ` (${((gap / together) * 100).toFixed(1)}%)`
      : "";
  // A document in another currency whose day's rate is not published yet: no
  // figure in the payment's to compare.
  const unrated = documents.some(
    (document) =>
      document.exchangeRateSource != null && document.exchangeRate == null,
  );
  // Short of a document another payment paid part of - or one its rule
  // matches a part of, the rest to come in another payment: an invoice paid in
  // two e-transfers - the rest still to come, not a fault.
  const settlesInPart = (
    presentation.matchDocumentDefinition?.settlementBases ?? []
  ).includes(MatchDocumentSettlementBasisEnum.BalancePartial);
  const isLeftToPay =
    !isOver && (paidBeforeTotal > 0 || settlesInPart || parts.length > 0);
  // What is left of the documents once the payments are set against them: what
  // the other payments offered under the table may pay.
  const rest = unrated ? 0 : together - paying;
  const untotalled: TriageSuggestionContextRow[] = [
    {
      key: "payment",
      kind: "payment",
      business: triage.entity,
      opens: {
        id: btoa(`TransactionObject:${triage.identifier}`),
        name: payment,
      },
      cells: {
        Amount: { value: formatAmount(amount) },
        Description: {
          value:
            against != null ? (
              <TriageSuggestionContextDifference
                side="payment"
                payment={payment}
                other={against || "—"}
              />
            ) : (
              payment
            ),
        },
        Date: {
          value: triage.effectiveDate
            ? Utils.fDate(new Date(triage.effectiveDate))
            : "—",
        },
      },
    },
    // Each payment the document is paid by already - moving off it, where the
    // match moves here, and then not counted.
    ...paidBefore.map(({ payment: other, paid }) => ({
      key: `other-payment:${other.identifier}`,
      kind: "other-payment" as const,
      business: other.entity,
      isMovedOff: !bothPaid,
      opens: {
        id: other.id,
        name: other.description?.trim() || "the other payment",
      },
      cells: {
        Amount: bothPaid
          ? { value: formatAmount(paid) }
          : { value: <s>{formatAmount(paid)}</s>, notes: ["Moves off"] },
        Description: { value: other.description?.trim() || "—" },
        Date: {
          value: other.effectiveDate
            ? Utils.fDate(new Date(other.effectiveDate))
            : "—",
        },
      },
    })),
    // Each other payment added to pay the rest of it: booked with this one.
    ...partsBookedWith.map(({ transaction: part }) => ({
      key: `part:${part.identifier}`,
      kind: "other-payment" as const,
      business: part.entity,
      removes: {
        label: `Take ${part.description?.trim() || "the other payment"} off`,
        onRemove: () =>
          setAdded(
            added.filter((identifier) => identifier !== part.identifier),
          ),
      },
      opens: {
        id: part.id,
        name: part.description?.trim() || "the other payment",
      },
      cells: {
        Amount: { value: formatAmount(Number(part.amount ?? 0)) },
        Description: {
          value: `${part.description?.trim() || "—"}${part.journalEntry ? " · booked already" : ""}`,
        },
        Date: {
          value: part.effectiveDate
            ? Utils.fDate(new Date(part.effectiveDate))
            : "—",
        },
      },
    })),
    ...(job === "MATCH_DOCUMENT"
      ? documents.map((document) => {
          const total =
            document.amountTotal != null ? Number(document.amountTotal) : null;
          const due =
            document.amountDue != null ? Number(document.amountDue) : null;
          // Its own day; with none, when it was taken in - said as that, not
          // passed off as the day it is of.
          const date = document.dateIssued ?? null;
          return {
            key: document.id,
            kind: "document" as const,
            business: document.entity,
            lineItemsOf: document.id,
            opens: {
              id: document.id,
              name: document.title,
            },
            cells: {
              Amount: {
                // In another currency than the payment's: what it prints, in
                // its own, then the sum that turns it into the payment's at
                // the official rate - its figure as the payment's is, the
                // working greyed - or, the day's rate not published yet, that
                // there is none.
                value:
                  document.exchangeRateSource != null ? (
                    <>
                      <span className="block">
                        {formatAmount(ownTotalOf(document), document.currency)}
                      </span>
                      <span
                        data-part="converted"
                        title={document.exchangeRateSource ?? undefined}
                        className="block whitespace-nowrap"
                      >
                        <span className="text-muted-foreground/80">
                          {document.exchangeRate != null
                            ? `× ${document.exchangeRate.toFixed(4)} = `
                            : `No ${document.exchangeRateSource} rate yet`}
                        </span>
                        {document.exchangeRate != null ? (
                          // In bold: what is set against the payment.
                          <span className="font-semibold">
                            {formatAmount(totalOf(document))}
                          </span>
                        ) : null}
                      </span>
                    </>
                  ) : (
                    formatAmount(totalOf(document))
                  ),
                // What of it is still owing, when some was paid before - and
                // not said where its payments are rows of their own.
                notes:
                  paidBefore.length === 0 &&
                  total != null &&
                  due != null &&
                  due < total
                    ? [
                        due <= 0
                          ? // Booked, the document it paid: paid by it.
                            triage.isBooked
                            ? "Paid by this payment"
                            : "Already paid"
                          : `${formatAmount(due, document.currency)} still owing`,
                      ]
                    : [],
              },
              // The tax it charges, beside its total: its lines before tax
              // and the tax on them come to it.
              ...(Number(document.amountTax ?? 0) !== 0 && {
                Tax: {
                  value: `${Number(document.amountTax) < 0 ? "-" : ""}${formatAmount(
                    Number(document.amountTax),
                    document.currency,
                  )}`,
                },
              }),
              Description: {
                // Its number first - what it is matched against - then its
                // name, where that says more.
                value: document.reference
                  ? document.title.includes(document.reference)
                    ? document.title
                    : `#${document.reference} · ${document.title}`
                  : document.title,
                notes: offeredElsewhere[document.id] ?? [],
              },
              Date: {
                value: date
                  ? Utils.fDate(new Date(date))
                  : document.createdAt
                    ? `Imported ${Utils.fDate(new Date(document.createdAt))}`
                    : "—",
              },
            },
          };
        })
      : others.map(({ comparison, other }) => ({
          key: comparison.key,
          kind: "other" as const,
          business: other.entity,
          opens: {
            id: other.id,
            name: other.description?.trim() || "the other payment",
          },
          cells: {
            Amount: { value: formatAmount(Number(other.amount ?? 0)) },
            Description: {
              value: (
                <TriageSuggestionContextDifference
                  side="other"
                  payment={payment}
                  other={other.description?.trim() || "—"}
                />
              ),
            },
            Date: {
              value: other.effectiveDate
                ? Utils.fDate(new Date(other.effectiveDate))
                : "—",
            },
          },
        }))),
    // How they match, a column at a time: the payments together against the
    // documents together.
    {
      key: "match",
      kind: "match",
      cells: {
        Amount:
          job === "MATCH_DOCUMENT"
            ? {
                verdict: unrated
                  ? { text: "No rate", matched: false }
                  : {
                      text:
                        gap < 0.005
                          ? "Exact"
                          : `${formatAmount(gap)} ${isLeftToPay ? "left" : isSeveral ? (isOver ? "over" : "short") : "apart"}${share}`,
                      // Agreeing as the rule that compared a lone document
                      // with this payment says, where one did.
                      matched: isSeveral
                        ? gap < 0.005
                        : (verdictFor("amount")?.matched ?? gap < 0.005),
                      isNeutral: gap >= 0.005 && isLeftToPay,
                    },
              }
            : { verdict: verdictFor("amount") },
        Description: { verdict: verdictFor("description") },
        Date: { verdict: verdictFor("date") },
      },
    },
    // Under the table, apart from what it books: the other payments that may
    // pay the rest, none added until one is - each no more than what is left.
    // One that fits is said to while those added fit too.
    ...(detail && rest > 0.005
      ? parts
          .filter(
            ({ transaction: part }) =>
              !added.includes(part.identifier) &&
              Math.abs(Number(part.amount ?? 0)) <= rest + 0.005,
          )
          .map(({ transaction: part, fits }) => ({
            key: `candidate:${part.identifier}`,
            kind: "candidate" as const,
            business: part.entity,
            adds: {
              label: `Add ${part.description?.trim() || "the other payment"}`,
              onAdd: () => setAdded([...added, part.identifier]),
            },
            cells: {
              Amount: {
                value: formatAmount(Number(part.amount ?? 0)),
                verdict:
                  fits && partsBookedWith.every((one) => one.fits)
                    ? { text: "Fits", matched: true }
                    : null,
              },
              Description: {
                value: `${part.description?.trim() || "—"}${part.journalEntry ? " · booked already" : ""}`,
              },
              Date: {
                value: part.effectiveDate
                  ? Utils.fDate(new Date(part.effectiveDate))
                  : "—",
              },
            },
          }))
      : []),
  ];
  // Taxed, each row is added across: what it said under Amount - and how the
  // two sides compare - is its total, and a document's amount is what it is
  // before its tax.
  const rows = isTaxed
    ? untotalled.map((row) => {
        const document = documents.find((each) => each.id === row.lineItemsOf);
        return {
          ...row,
          cells: {
            ...row.cells,
            Total: row.cells.Amount,
            Amount: {
              value: document
                ? formatAmount(
                    Number(document.amountTotal ?? document.amountDue ?? 0) -
                      Number(document.amountTax ?? 0),
                    document.currency,
                  )
                : row.cells.Amount.value,
            },
          },
        };
      })
    : untotalled;

  return (
    <div
      data-testid="triage-suggestion-context"
      data-job={job}
      className="flex flex-col min-w-0"
    >
      <TriageSuggestionContextTable
        rows={rows}
        isTaxed={isTaxed}
        // Written as its document's are: in its own currency.
        formatLineAmount={(documentId, value) =>
          formatAmount(
            value,
            documents.find((document) => document.id === documentId)
              ?.currency ?? paidIn,
          )
        }
        restHeading={`May pay the rest · ${formatAmount(rest)}`}
        criteria={
          (suggestion.source ?? "").toLowerCase() === "rule" &&
          suggestion.rule ? (
            <TriageSuggestionContextRuleCriteria
              suggestion={suggestion}
              triage={triage}
              presentation={presentation}
            />
          ) : undefined
        }
      />
    </div>
  );
}

/** A cell of a suggestion's table. */
type TriageSuggestionContextCell = {
  value?: ReactNode;
  /** How the column compares, in the match row: in green where it agrees, red where it does not - grey where neither, as what is left to pay of a part-paid document. */
  verdict?: { text: string; matched: boolean; isNeutral?: boolean } | null;
  /** Said under the value, in red: what is still owing on a document, and who else is offered it. */
  notes?: string[];
};

/** A row of a suggestion's table: a payment, a document, how they match. */
export type TriageSuggestionContextRow = {
  key: string;
  kind:
    | "payment"
    | "other"
    | "other-payment"
    | "document"
    | "match"
    | "candidate";
  /** Whose it is: the rows of one business, one after another, in a box with its name on it. */
  business?: { identifier?: string | null; name?: string | null } | null;
  /** Faded: a payment the match moves off. */
  isMovedOff?: boolean;
  /** Taken off by its ×: another payment added to pay the rest of the document. */
  removes?: { label: string; onRemove: () => void };
  /** Added by its +: another payment that may pay the rest of it, offered. */
  adds?: { label: string; onAdd: () => void };
  /** The document whose lines are rows of their own over it. */
  lineItemsOf?: string;
  /** The payment or document the row is, opened in the panel from its words. */
  opens?: {
    id: string;
    name: string;
  };
  cells: Record<
    Exclude<TableColumn, "Tax" | "Total">,
    TriageSuggestionContextCell
  > & {
    Tax?: TriageSuggestionContextCell;
    Total?: TriageSuggestionContextCell;
  };
};

/**
 * The table a suggestion opens to: a row for each payment and document, each
 * opened in the panel from its words, and how they match, a column at a time -
 * a tick in green where it agrees, a warning in red where it does not. Drawn
 * for a suggestion (``TriageSuggestionContext``) and for a document lined up
 * with a payment by hand (``TriageDocumentPairs``).
 */
export function TriageSuggestionContextTable({
  rows,
  isTaxed = false,
  formatLineAmount,
  restHeading,
  criteria,
  onOpen,
}: {
  rows: TriageSuggestionContextRow[];
  /** Each row added across: a Tax and a Total column beside its Amount. */
  isTaxed?: boolean;
  /** A document's lines, over its row, written as its own figures are. */
  formatLineAmount: (documentId: string, value: number) => string;
  /** Over the other payments that may pay the rest. */
  restHeading?: string;
  /** Beside the match row's name: what the rule that made it matches on. */
  criteria?: ReactNode;
  /** Where a row opened from its words opens - the app's panel, unless a dialog over it has its own. */
  onOpen?: (opens: NonNullable<TriageSuggestionContextRow["opens"]>) => void;
}) {
  // What differs is said in the app's own red, the one an error wears, in the
  // shade the colour scheme calls for.
  const red = useEnumColor(EnumColors.RED);
  // And what agrees, in the green a success wears.
  const green = useEnumColor(EnumColors.GREEN);
  const columns = TABLE_COLUMNS.filter(
    (column) => (column !== "Tax" && column !== "Total") || isTaxed,
  );
  // A row as the table draws it: a document's lines over it, as a
  // receipt's stand over its total, then it.
  const drawRow = (row: TriageSuggestionContextRow) => (
    <Fragment key={row.key}>
      {/* A document's lines over it, as a receipt's stand over its
            total. */}
      {row.lineItemsOf ? (
        <TriageSuggestionContextLineItems
          documentId={row.lineItemsOf}
          columns={columns}
          formatAmount={(value) => formatLineAmount(row.lineItemsOf!, value)}
        />
      ) : null}
      <div
        data-testid="triage-suggestion-table-row"
        data-kind={row.kind}
        data-moved-off={row.isMovedOff || undefined}
        // Its cells set in from the card's edge at its ends: they, not
        // the card, so each row's line runs out to the card's edge.
        className={cn(
          "contents [&>*]:pt-1.5 [&>*]:pb-1.5 [&>*]:pl-3 [&>*]:pr-3 [&>*:first-child]:pl-5 [&>*]:[border-top-width:1px] [&>*]:[border-top-style:solid] [&>*]:border-border",
          row.isMovedOff && "[&>*]:opacity-[0.5]",
        )}
      >
        {columns.map((column) => {
          const cell = row.cells[column] ?? {};
          const end =
            column === "Amount" || column === "Tax" || column === "Total";
          // How it compares, said by its mark alone: a tick in green
          // where it agrees, a warning in red where it does not.
          const mark =
            cell.verdict && !cell.verdict.isNeutral ? (
              <span
                className="flex text-(--verdict-color)"
                style={
                  {
                    "--verdict-color": cell.verdict.matched
                      ? green.color
                      : red.color,
                  } as CSSProperties
                }
              >
                <Icon
                  ReactIcon={cell.verdict.matched ? CheckIcon : CircleAlertIcon}
                  size="xs"
                />
              </span>
            ) : undefined;
          return (
            <div
              key={column}
              data-column={column}
              className={cn("min-w-0", end ? "text-right" : "text-left")}
            >
              {cell.value != null && cell.value !== "" ? (
                <Typography
                  level="body-sm"
                  component="div"
                  data-part="value"
                  // Offered, not booked: quieter than what is.
                  // The payment being looked at, in bold across - or,
                  // where each row is added across, what each comes
                  // to in bold down, and no row bold.
                  className={cn(
                    "flex items-center gap-1.5",
                    // A flex box takes no ``text-right``: set to its
                    // column's side by its own, as its head, its lines
                    // and the verdict under it are.
                    end && "justify-end",
                    "wrap-anywhere",
                    row.kind === "match" && "text-muted-foreground/80",
                    row.kind === "candidate" && "text-muted-foreground",
                    (isTaxed ? column === "Total" : row.kind === "payment") &&
                      "font-semibold",
                  )}
                >
                  {column === "Description" && row.opens ? (
                    // The row, opened in the panel from its words.
                    <button
                      type="button"
                      title={`Open ${row.opens.name}`}
                      data-testid="triage-suggestion-open"
                      // Pointing the panel at it is not opening it.
                      onClick={() =>
                        onOpen
                          ? onOpen(row.opens!)
                          : State.getState().openNodePanel(row.opens!.id, {
                              autofocus: false,
                            })
                      }
                      className="[font:inherit] text-inherit text-left min-w-0 wrap-anywhere cursor-pointer underline-offset-4 hover:underline"
                    >
                      {cell.value}
                    </button>
                  ) : (
                    cell.value
                  )}
                  {column === "Description" && row.removes ? (
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      aria-label={row.removes.label}
                      data-testid="triage-suggestion-part-remove"
                      onClick={row.removes.onRemove}
                    >
                      <Icon ReactIcon={XIcon} size="sm" />
                    </Button>
                  ) : column === "Description" && row.adds ? (
                    // Offered: added to pay the rest by its +, as one added is taken off by its ×.
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          aria-label={row.adds.label}
                          data-testid="triage-suggestion-part-add"
                          onClick={row.adds.onAdd}
                        >
                          <PlusIcon />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top">Add</TooltipContent>
                    </Tooltip>
                  ) : undefined}
                </Typography>
              ) : null}
              {(cell.notes ?? []).map((note) => (
                <Typography
                  key={note}
                  level="body-xs"
                  className="text-(--note-color)"
                  style={{ "--note-color": red.color } as CSSProperties}
                >
                  {note}
                </Typography>
              ))}
              {/* What the rule that made the match matches on, at its start -
                  after how its words compare, where they are. */}
              {column === "Description" &&
              row.kind === "match" &&
              !cell.verdict ? (
                // On a line of the table's words, as tall as theirs.
                <Typography
                  level="body-sm"
                  component="div"
                  className="flex items-center min-h-[1lh]"
                >
                  {criteria}
                </Typography>
              ) : null}
              {cell.verdict ? (
                // Its words plain, its mark coloured.
                <Typography
                  level="body-sm"
                  component="span"
                  data-part="verdict"
                  data-matched={cell.verdict.matched}
                  data-neutral={cell.verdict.isNeutral || undefined}
                  // Set to its column's side, as the values over it
                  // are: a cell is no flex box, so its margin does it.
                  // One line, as wide as it says.
                  // Its words the values' own colour: its mark says
                  // how it compares.
                  className={cn(
                    "flex items-center gap-1.5",
                    "flex",
                    end ? "ml-auto" : "ml-0",
                    "w-fit whitespace-nowrap text-muted-foreground",
                  )}
                >
                  {end ? mark : undefined}
                  {cell.verdict.text}
                  {end ? undefined : mark}
                  {column === "Description" && row.kind === "match"
                    ? criteria
                    : undefined}
                </Typography>
              ) : null}
            </div>
          );
        })}
      </div>
    </Fragment>
  );
  // Each business's rows, one after another, together: a run in a box with
  // its name on it. The other payments that may pay the rest start one of
  // their own, under their heading.
  const runs: {
    business: TriageSuggestionContextRow["business"];
    rows: TriageSuggestionContextRow[];
  }[] = [];
  rows.forEach((row, index) => {
    const run = runs[runs.length - 1];
    const isRestFirst =
      row.kind === "candidate" && rows[index - 1]?.kind !== "candidate";
    const business =
      row.business?.identifier && row.business.name ? row.business : null;
    if (
      run &&
      !isRestFirst &&
      business &&
      run.business?.identifier === business.identifier
    ) {
      run.rows.push(row);
    } else {
      runs.push({ business, rows: [row] });
    }
  });
  return (
    <div
      data-testid="triage-suggestion-table"
      className={cn(
        // Too narrow for its columns - a phone - it scrolls across in its
        // own box, rather than its description wrapping a letter or two a
        // line; it ends at its last row's own room, as much under it as over
        // its head. No gaps between cells, so a row's line runs unbroken
        // across it: cells hold themselves apart instead.
        "grid grid-cols-(--table-columns) overflow-x-auto [align-items:start] min-w-0",
        TABLE_LINES_CLASS,
        // Under a business's box, no line over the row: its edge is that.
        "[&>[data-testid='triage-suggestion-table-business']+[data-testid='triage-suggestion-table-row']>*]:[border-top:none]",
      )}
      style={
        {
          "--table-columns": columns
            .map((column) => TABLE_COLUMN_WIDTHS[column])
            .join(" "),
        } as CSSProperties
      }
    >
      {columns.map((column, index) => (
        <Typography
          key={column}
          level="body-xs"
          data-testid="triage-suggestion-table-head"
          className={cn(
            "text-muted-foreground/80 font-semibold uppercase [letter-spacing:0.06em] pb-1.5",
            // Each column as far from the next as the last is from the
            // table's edge - the first further in, clear of a business's
            // box round its rows.
            index === 0 ? "pl-5 pr-3" : "pl-3 pr-3",
            column === "Amount" || column === "Tax" || column === "Total"
              ? "text-right"
              : "text-left",
          )}
        >
          {column}
        </Typography>
      ))}
      {runs.map((run) => {
        const first = run.rows[0];
        const before = rows[rows.indexOf(first) - 1];
        return (
          <Fragment key={first.key}>
            {/* The other payments that may pay the rest, under a heading of
                their own: apart from what is booked, until one is added. */}
            {first.kind === "candidate" && before?.kind !== "candidate" ? (
              <Typography
                level="body-xs"
                data-testid="triage-suggestion-parts-heading"
                className="text-muted-foreground/80 font-semibold uppercase [letter-spacing:0.06em] col-span-full pt-3 pb-1.5 pl-5 pr-3 [border-top-width:1px] [border-top-style:dashed] border-border"
              >
                {restHeading}
              </Typography>
            ) : null}
            {run.business ? (
              <div
                data-testid="triage-suggestion-table-business"
                data-business={run.business.name ?? undefined}
                className={cn(
                  // The table's own columns, so what is in it lines up with
                  // what is not - its outline drawn over its rows, not round
                  // them, half a cell's room in from the table's edge.
                  "col-span-full grid grid-cols-subgrid [align-items:start] relative mt-3 mb-1",
                  "before:content-[''] before:absolute before:inset-x-1.5 before:inset-y-0 before:rounded-lg before:border before:border-border before:pointer-events-none",
                  // Its name on its top edge, on the table's ground.
                  "after:content-[attr(data-business)] after:absolute after:-top-2 after:left-4 after:px-1 after:text-[11px] after:leading-4 after:font-semibold after:text-muted-foreground after:bg-muted dark:after:bg-secondary",
                  // The lines between its rows end at its outline, and none
                  // is over its first: its edge is that line, its name over it.
                  "[clip-path:inset(-1rem_6px_0_6px)]",
                  "[&>:first-child>*]:[border-top:none] [&>:first-child>*]:pt-2.5",
                  TABLE_LINES_CLASS,
                )}
              >
                {run.rows.map(drawRow)}
              </div>
            ) : (
              run.rows.map(drawRow)
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

/**
 * What the rule that made a suggestion matches on, behind a "?" beside the
 * match row's name: which payments it looks at - its conditions, a line each -
 * and, for a document rule, what it compares a document on.
 */
function TriageSuggestionContextRuleCriteria({
  suggestion,
  triage,
  presentation,
}: {
  suggestion: TriageSuggestionStub;
  triage: TriageDetail;
  presentation: ReturnType<typeof buildChangelistProposalPresentation>;
}) {
  // Its top-level "and" conditions a line each; anything else as one.
  const group = ruleConditionsToFilterGroup(suggestion.rule?.conditions ?? []);
  const parts =
    group.type === "and" && !group.not
      ? group.children.map((child) =>
          isFilterGroup(child)
            ? child
            : { type: "and" as const, children: [child] },
        )
      : [group];
  // The payment's bank account said by its name, not its identifier.
  const account =
    triage.financialAccount && "chartedAccount" in triage.financialAccount
      ? {
          identifier: triage.financialAccount.chartedAccount?.identifier,
          label:
            triage.financialAccount.name?.trim() ||
            triage.financialAccount.chartedAccount?.name?.trim(),
        }
      : null;
  const when = [
    ...new Set(
      parts.flatMap((part) => {
        const line = ruleWhenDescriptionBody(
          suggestion.rule,
          presentation.job,
          part.children.length > 0 ? part : undefined,
          true,
        );
        if (!line) return [];
        return [
          account?.identifier && account.label
            ? line.split(account.identifier).join(account.label)
            : line,
        ];
      }),
    ),
  ];
  const find =
    presentation.job === "MATCH_DOCUMENT" &&
    presentation.matchDocumentDefinition != null
      ? matchDocumentFindCriteriaLines(
          presentation.matchDocumentDefinition,
          presentation.configuredCriteria,
        )
      : [];

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon-xs"
          aria-label="What the rule matches on"
          data-testid="triage-suggestion-rule-criteria-help"
          // Its line's height, over which it reaches: taller, its row was,
          // and the room under every other cell of it with it.
          className="-my-1 text-muted-foreground/80 hover:text-muted-foreground"
        >
          <Icon ReactIcon={CircleQuestionMarkIcon} size="xs" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top">
        <div
          data-testid="triage-suggestion-rule-criteria"
          className="max-w-80 pt-1 pb-1 flex flex-col gap-2"
        >
          <Typography level="title-sm">
            {suggestion.rule?.name?.trim() || "Its rule"}
          </Typography>
          {(
            [
              ["When", when],
              ["Matches on", find],
            ] as const
          ).map(([heading, lines]) =>
            lines.length > 0 ? (
              <div key={heading}>
                <Typography
                  level="body-xs"
                  className="text-muted-foreground/80 font-semibold uppercase [letter-spacing:0.06em]"
                >
                  {heading}
                </Typography>
                {lines.map((line) => (
                  <Typography
                    key={line}
                    level="body-sm"
                    className="text-muted-foreground"
                  >
                    {line}
                  </Typography>
                ))}
              </div>
            ) : null,
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

/** Lines a document's lines stand in for while they load: a typical few. */
const LINE_ITEM_PLACEHOLDERS = [
  { id: "placeholder-1", description: "A line of the bill", amount: "100.00" },
  { id: "placeholder-2", description: "Another line", amount: "20.00" },
];

/**
 * What a document is for, line by line: rows of a suggestion's table over
 * the document's, as a receipt's lines stand over its total, each in the
 * table's own columns - what it is under the
 * description, its amount under the amount. Asked for as the suggestion
 * opens - never with the list of them - its first few lines, then how many
 * more, which open the document.
 */
function TriageSuggestionContextLineItems({
  documentId,
  columns,
  formatAmount,
}: {
  documentId: string;
  /** The table's columns: its lines are rows of it. */
  columns: TableColumn[];
  /** Writes an amount of it as the table writes its document's. */
  formatAmount: (value: number) => string;
}) {
  const { data, loading } = useBackendQuery(Queries.DOCUMENT_LINE_ITEMS_QUERY, {
    variables: { id: documentId },
  });
  const items =
    data?.node?.__typename === "DocumentObject"
      ? (data.node.lineItems ?? [])
      : [];
  if (!loading && items.length === 0) return null;
  const shown: Array<{
    id: string;
    description?: string | null;
    amount?: unknown;
    amountTax?: unknown;
    quantity?: unknown;
  }> = loading ? LINE_ITEM_PLACEHOLDERS : items.slice(0, 4);
  // With its sign: a credit on it - unused time, a discount - takes off what
  // the rest come to.
  const formatSigned = (value: unknown) =>
    `${Number(value) < 0 ? "-" : ""}${formatAmount(Number(value))}`;

  // Rows of the table, over the document: no line between them and it.
  const lineRowClassName =
    "contents [&>*]:pl-3 [&>*]:pr-3 [&>*:first-child]:pl-5 [&>*]:pb-1 [&>*]:min-w-0";

  return (
    <>
      {shown.map((item) => (
        <div
          key={item.id}
          data-testid="triage-suggestion-line-item"
          data-part="line"
          className={lineRowClassName}
        >
          {columns.map((column) => (
            <div
              key={column}
              data-column={column}
              className={cn(
                column === "Amount" || column === "Tax" || column === "Total"
                  ? "text-right"
                  : "text-left",
              )}
            >
              {column === "Description" ||
              column === "Amount" ||
              column === "Total" ||
              (column === "Tax" && item.amountTax != null) ? (
                <Typography
                  level="body-xs"
                  className={cn(
                    "truncate",
                    "text-muted-foreground",
                    column === "Total" && "font-semibold",
                  )}
                >
                  <Skeleton loading={loading} variant="inline">
                    {column === "Description"
                      ? `${item.description?.trim() || "Line item"}${
                          Number(item.quantity ?? 1) > 1
                            ? ` × ${Number(item.quantity)}`
                            : ""
                        }`
                      : column === "Tax"
                        ? formatSigned(item.amountTax)
                        : column === "Total"
                          ? formatSigned(
                              Number(item.amount ?? 0) +
                                Number(item.amountTax ?? 0),
                            )
                          : formatSigned(item.amount ?? 0)}
                  </Skeleton>
                </Typography>
              ) : null}
            </div>
          ))}
        </div>
      ))}
      {!loading && items.length > 4 ? (
        <div data-part="line" className={lineRowClassName}>
          {columns.map((column) => (
            <div key={column}>
              {column === "Description" ? (
                <button
                  type="button"
                  data-testid="triage-suggestion-line-items-more"
                  onClick={() =>
                    State.getState().openNodePanel(documentId, {
                      autofocus: false,
                    })
                  }
                  className={cn(
                    typographyVariants({
                      level: "body-xs",
                    }),
                    "text-muted-foreground underline-offset-4 hover:underline cursor-pointer",
                    "text-muted-foreground/80",
                  )}
                >
                  + {items.length - 4} more
                </button>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </>
  );
}

/**
 * Two values side by side, marked where they differ the way a diff marks
 * them: on the left, in red, what the other does not have; on the right, in
 * green, what it has instead. A word that was replaced is both. What the two
 * share is left alone, so the eye goes to REF8842 against REF8841 and not to
 * the forty characters before it.
 *
 * Compared word by word, and a word replaced by one other word is compared
 * again letter by letter from both ends, so 8842 against 8841 marks a digit.
 */
function TriageSuggestionContextDifference({
  side,
  payment,
  other,
}: {
  /** Which of the two this is, on its own row: the payment's, or the other's. */
  side: "payment" | "other";
  payment: string;
  other: string;
}) {
  const red = useEnumColor(EnumColors.RED);
  const green = useEnumColor(EnumColors.GREEN);

  const left: Array<{ text: string; differs: boolean }> = [];
  const right: Array<{ text: string; differs: boolean }> = [];
  // A bank writes SUMMIT CATERING and a person Summit catering: the same
  // words, and not what anyone is looking for.
  const same = (one: string, another: string) =>
    one.toLowerCase() === another.toLowerCase();
  if (same(payment, other)) {
    left.push({ text: payment, differs: false });
    right.push({ text: other, differs: false });
  } else {
    const before = payment.split(/(\s+)/).filter(Boolean);
    const after = other.split(/(\s+)/).filter(Boolean);
    // The longest run of words the two share, from each position to the end.
    const shared = before.map(() => after.map(() => 0));
    const at = (i: number, j: number) => shared[i]?.[j] ?? 0;
    for (let i = before.length - 1; i >= 0; i--) {
      for (let j = after.length - 1; j >= 0; j--) {
        shared[i][j] = same(before[i], after[j])
          ? at(i + 1, j + 1) + 1
          : Math.max(at(i + 1, j), at(i, j + 1));
      }
    }
    // Between two shared words: what only the left has, what only the right.
    let removed: string[] = [];
    let added: string[] = [];
    const settle = () => {
      const [gone, come] = [removed.join("").trim(), added.join("").trim()];
      if (gone && come && !/\s/.test(gone) && !/\s/.test(come)) {
        let head = 0;
        while (head < gone.length && same(gone[head], come[head] ?? "")) head++;
        let tail = 0;
        while (
          tail < gone.length - head &&
          tail < come.length - head &&
          same(gone[gone.length - 1 - tail], come[come.length - 1 - tail])
        ) {
          tail++;
        }
        // Mostly the same word: mark the letters. A different word: mark it.
        const keeps = head + tail >= Math.min(gone.length, come.length) / 2;
        for (const [side, word, source] of [
          [left, gone, removed],
          [right, come, added],
        ] as const) {
          const lead = source.join("").match(/^\s*/)?.[0] ?? "";
          const trail = source.join("").match(/\s*$/)?.[0] ?? "";
          if (keeps) {
            side.push(
              { text: lead + word.slice(0, head), differs: false },
              { text: word.slice(head, word.length - tail), differs: true },
              { text: word.slice(word.length - tail) + trail, differs: false },
            );
          } else {
            side.push(
              { text: lead, differs: false },
              { text: word, differs: true },
              { text: trail, differs: false },
            );
          }
        }
      } else {
        for (const [side, source] of [
          [left, removed],
          [right, added],
        ] as const) {
          const text = source.join("");
          if (!text) continue;
          side.push(
            { text: text.match(/^\s*/)?.[0] ?? "", differs: false },
            { text: text.trim(), differs: true },
            {
              text: text.trim() ? (text.match(/\s*$/)?.[0] ?? "") : "",
              differs: false,
            },
          );
        }
      }
      removed = [];
      added = [];
    };
    let i = 0;
    let j = 0;
    while (i < before.length || j < after.length) {
      if (i < before.length && j < after.length && same(before[i], after[j])) {
        settle();
        left.push({ text: before[i], differs: false });
        right.push({ text: after[j], differs: false });
        i++;
        j++;
      } else if (
        j >= after.length ||
        (i < before.length && at(i + 1, j) >= at(i, j + 1))
      ) {
        removed.push(before[i++]);
      } else {
        added.push(after[j++]);
      }
    }
    settle();
  }

  const [parts, mark, colour] =
    side === "payment"
      ? ([left, "removed", red] as const)
      : ([right, "added", green] as const);
  return (
    <>
      {parts
        .filter((part) => part.text)
        .map((part, index) =>
          part.differs ? (
            <span
              key={index}
              data-testid={`triage-suggestion-difference-${mark}`}
              // A hair of room, so a single marked digit does not stand
              // apart from the rest of its number.
              className="rounded pl-px pr-px [box-decoration-break:clone] text-(--difference-color) bg-(--difference-background)"
              style={
                {
                  "--difference-color": colour.color,
                  "--difference-background": colour.backgroundColor,
                } as CSSProperties
              }
            >
              {part.text}
            </span>
          ) : (
            part.text
          ),
        )}
    </>
  );
}
